import { useEffect } from 'react';

import OnboardingPackets from 'components/practice/Settings/components/Forms/FormPackets/OnboardingPackets';
import OnboardingPayers from 'components/practice/Settings/components/Payers/OnboardingPayers';
import StepperReview from 'components/shared/Stepper/components/Review/StepperReview';

import OnboardingRoles from '../../../Roles/OnboardingRoles';
import OnboardingClinicalForms from '../../../admin/pages/Practices/FormBuilder/OnboardingClinicalForms';
import Responsibilities from '../components/Acknowledgements/Responsibilities';
import TermsAndConditions from '../components/Acknowledgements/TermsAndConditions';
import AddressForm from '../components/AddressForm/AddressForm';
import BillingTable from '../components/BillingTable/BillingTable';
import ContactInfoForm from '../components/ContactInforForm/ContactInfoForm';
import DataExport from '../components/DataExport/DataExport';
import DelegationTasks from '../components/DelegationTasks/DelegationTasks';
import GoLiveDatesAndTrainings from '../components/GoLiveDatesAndTrainings/GoLiveDatesAndTrainings';
import IntroStep from '../components/IntroStep/IntroStep';
import LiveTrainingStep from '../components/LiveTrainingStep/LiveTrainingStep';
import MainDetailsForm from '../components/MainDetailsForm/MainDetailsForm';
import OnboardingFacilityTable from '../components/OnboardingFacilityTable/OnboardingFacilityTable';
import OnboardingImports from '../components/OnboardingImports/OnboardingImports';
import ImportFromOrganization from '../components/OnboardingImports/components/ImportFromOrganization/ImportFromOrganization';
import AdminOrganizations from '../components/Organization/AdminOrganization';
import PracticeBrandingForm from '../components/PracticeBrandingForm/PracticeBrandingForm';
import PracticeSpecialty from '../components/PracticeSpecialty/PracticeSpecialty';
import ScheduleOfFees from '../components/ScheduleOfFees/ScheduleOfFees';
import SupportForm from '../components/SupportForm/SupportForm';
import TrainingVideo from '../components/TrainingVideo/TrainingVideo';
import WizardEprescribe from '../components/WizardEprescribe/WizardEprescribe';
import WizardKiosks from '../components/WizardKiosks/WizardKiosks';
import WizardStaff from '../components/WizardStaff/WizardStaff';
import withWizardStep from '../components/withWizardStep';

export const StepPlaceholder = withWizardStep((props) => {
  const { onPrev, onNext, updateConfigs, substepCode, setHeaderButtonProps } = props;
  useEffect(() => {
    setHeaderButtonProps(null);
    updateConfigs({
      message: 'This step is not yet configured.',
      goBackHandler: onPrev,
      actions: [
        {
          text: 'Skip for now',
          onClick: onNext,
          outlined: true
        }
      ]
    });
  }, [substepCode]);
  return (
    <div className="flex w-full justify-center">
      <div className="w-full max-w-[70%] text-center text-neutral-500">
        This step is not yet configured.
      </div>
    </div>
  );
});

export default {
  onboarding__intro__intro: {
    component: IntroStep,
    headerOptions: {
      stepTitle: 'Welcome to Myriad Motion',
      stepSubtitle: 'Your onboarding journey starts here.',
      buttonText: null
    },
    smart_suggestions: [
      'Take a moment to explore what Myriad Motion offers and how it will transform your onboarding experience.',
      'This introduction sets the foundation for your personalized practice setup journey.',
      'Click "Start Myriad Motion" when you\'re ready to begin configuring your practice.'
    ]
  },
  onboarding__support_and_contact__support_and_contact: {
    component: withWizardStep(SupportForm),
    headerOptions: {
      stepTitle: 'Support & contact',
      stepSubtitle:
        'Your Myriad team is here to guide and support your practice through onboarding and beyond.',
      buttonText: 'Contact Myriad Support'
    },
    smart_suggestions: [
      'Your dedicated Myriad support team will assign consultants and onboarding specialists who will guide you through setup and ongoing success.',
      "If you don't see your contacts listed yet, they'll appear here once your setup team assigns them—no action is needed from your side.",
      'You can reach support anytime to explore or enable additional services like MedBill or the Virtual Office Manager.'
    ]
  },
  onboarding__go_live_dates_and_trainings__go_live_dates_and_trainings: {
    component: withWizardStep(GoLiveDatesAndTrainings),
    headerOptions: {
      stepTitle: 'Go-live dates & trainings',
      stepSubtitle:
        'In application trainings will be completed ahead of live trainings. If additional or longer trainings are needed, please do not hesitate to make this request to your dedicated Onboarding Solutions Engineer.',
      buttonText: 'Set Go-Live Dates & Trainings'
    },
    smart_suggestions: [
      'Review and confirm your go-live dates carefully — these mark when your practice officially starts using Myriad Health with real patient appointments.',
      'The hard launch date is your confirmed launch schedule; onboarding must be completed before this date to ensure a smooth transition.',
      'Acknowledge the training requirements to confirm that your team will attend all scheduled sessions.'
    ]
  },
  onboarding__kickoff_video_training__kickoff_video_training: {
    component: withWizardStep(TrainingVideo),
    headerOptions: {
      stepTitle: 'Kickoff video training',
      stepSubtitle:
        'Kickoff training will provide an overview of the Myriad Motion platform and outline the onboarding process for your practice.',
      buttonText: 'Start Kickoff Training'
    }
  },
  onboarding__front_office_video_training__front_office_video_training: {
    component: withWizardStep(TrainingVideo),
    headerOptions: {
      stepTitle: 'Front office video training',
      stepSubtitle:
        'Front office training will provide an overview of the Myriad Motion platform and outline the onboarding process for your practice.',
      buttonText: 'Start Front Office Training'
    }
  },
  onboarding__clinical_video_training__clinical_video_training: {
    component: withWizardStep(TrainingVideo),
    headerOptions: {
      stepTitle: 'Clinical video training',
      stepSubtitle:
        'Clinical training will provide an overview of the Myriad Motion platform and outline the onboarding process for your practice.',
      buttonText: 'Start Clinical Training'
    }
  },
  onboarding__back_office_video_training__back_office_video_training: {
    component: withWizardStep(TrainingVideo),
    headerOptions: {
      stepTitle: 'Back office video training',
      stepSubtitle:
        'Back office training will provide an overview of the Myriad Motion platform and outline the onboarding process for your practice.',
      buttonText: 'Start Back Office Training'
    }
  },
  onboarding__hard_launch_video_training__hard_launch_video_training: {
    component: withWizardStep(TrainingVideo),
    headerOptions: {
      stepTitle: 'Hard launch video training',
      stepSubtitle:
        'Hard launch training will provide an overview of the Myriad Motion platform and outline the onboarding process for your practice.',
      buttonText: 'Start Hard Launch Training'
    }
  },
  onboarding__acknowledgements__terms_and_conditions: {
    component: withWizardStep(TermsAndConditions),
    headerOptions: {
      stepTitle: 'Terms & conditions',
      stepSubtitle:
        'The provisions below summarize key participation, processing, onboarding, and usage requirements associated with the MediPay + Myriad Health bundled offering. These summaries do not replace your executed agreements. In the event of conflict, signed agreements govern. Custom provisions may not be reflected here. Bundled Myriad Health access and subsidized onboarding services are conditioned upon compliance with MediPay processing and participation requirements outlined below.',
      buttonText: 'View Terms & Conditions'
    },
    smart_suggestions: [
      'Review each section carefully to understand how MediPay and Myriad work together for your practice.',
      "Check all boxes once you've read and agreed to the listed responsibilities.",
      'Accepting these terms ensures your onboarding and payment setup continue without delays.'
    ]
  },
  onboarding__acknowledgements__responsibilities: {
    component: withWizardStep(Responsibilities),
    headerOptions: {
      stepTitle: 'Responsibilities',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'View Responsibilities'
    },
    smart_suggestions: [
      'Set each due date to occur before its Live Training.',
      'To change a confirmed training date, contact your onboarding team.',
      'Being ready before training maximizes your time with our professionals.'
    ]
  },
  onboarding__basic_setup_confirmation__main_details: {
    component: withWizardStep(MainDetailsForm),
    headerOptions: {
      stepTitle: 'Main details',
      stepSubtitle:
        "Enter your practice's primary information to establish your Myriad Health profile.",
      buttonText: 'Save Main Details'
    },
    smart_suggestions: [
      'Use your legal practice name as on IRS/payor records; add a DBA only if patients see a different name.',
      'Enter your specialty taxonomy and choose the correct Tax ID type—EIN for organizations, SSN/ITIN for solo providers—to ensure claims and 1099s match.',
      'Select the practice kind (e.g., Medical, Dental) to tailor forms, services, and reporting from the start.'
    ]
  },
  onboarding__basic_setup_confirmation__address: {
    component: withWizardStep(AddressForm),
    headerOptions: {
      stepTitle: 'Address',
      stepSubtitle:
        "Enter your practice's primary information to establish your Myriad Health profile.",
      buttonText: 'Save Practice Address'
    },
    smart_suggestions: [
      'Enter the full street address of your primary practice location (include suite/unit if applicable).',
      'This address is used across setup—claims, billing, and patient messages—so ensure City, State, and ZIP are accurate.',
      'If you have multiple locations, start with your main site now; you can add more addresses later.'
    ]
  },
  onboarding__basic_setup_confirmation__contact_information: {
    component: withWizardStep(ContactInfoForm),
    headerOptions: {
      stepTitle: 'Contact information',
      stepSubtitle:
        'Add the key details your patients and partners need to easily reach your practice.',
      buttonText: 'Save contact information'
    },
    smart_suggestions: [
      'Your Twilio texting/voice line is auto-generated after your practice address is verified—if you see an error here, complete or correct the Address step.',
      'Fax is optional: click “Request Fax” to auto-provision a secure fax number only if your practice plans to send/receive faxes.',
      'Use a monitored practice email and main phone number; these drive patient notifications, reminders, and payer communications.'
    ]
  },
  onboarding__basic_setup_confirmation__branding: {
    component: withWizardStep(PracticeBrandingForm),
    headerOptions: {
      stepTitle: 'Branding',
      stepSubtitle:
        "Enter your practice's primary information to establish your Myriad Health profile.",
      buttonText: 'Save Branding'
    },
    smart_suggestions: [
      'Upload your practice logo—it will appear on receipts, emails, and patient-facing documents for consistent branding.',
      'Use a clear, square image (minimum 400x400px, PNG or JPEG) to ensure it displays well across all communications.',
      'Set your brand colors to match your logo; these will soon apply automatically to emails, patient portals, and printed materials.'
    ]
  },
  onboarding__basic_setup_confirmation__organization: {
    component: withWizardStep(AdminOrganizations),
    headerOptions: {
      stepTitle: 'Organization',
      stepSubtitle:
        'Select your organization to link it with your Myriad Health profile. You can choose from existing records or create a new one to continue setup.',
      buttonText: 'Save Organization'
    },
    smart_suggestions: [
      'If your practice belongs to a larger group or network, our staff will link you automatically—no action needed right now.',
      "If you don't operate under any organization, click 'Skip Organization' to continue your setup independently.",
      'Once linked, review and confirm your organization details to ensure your practice information and reporting stay aligned.'
    ]
  },
  onboarding__clearinghouse__speciality: {
    component: withWizardStep(PracticeSpecialty),
    headerOptions: {
      stepTitle: 'Specialty',
      stepSubtitle:
        "Arrange your billing specialties in order of priority, the ones at the top will be used first when submitting eligibility requests, which determines the patient's deductible, copayment, and coinsurance.",
      buttonText: 'Save Specialties'
    },
    smart_suggestions: [
      'Your specialties determine how patient estimates, Good Faith Estimates (GFEs), and payer benefits are calculated.',
      'For best results, include multiple specialties - from most specific to most general (for example, listOrthopedics before General Medicine).'
    ]
  },
  onboarding__staff_and_permissions__roles_and_permissions: {
    component: withWizardStep(OnboardingRoles),
    headerOptions: {
      stepTitle: 'Roles & permissions',
      stepSubtitle:
        'Manage team access by assigning roles and permissions within your Myriad Health organization. Select from existing roles or create new ones to fit your practice’s structure.',
      buttonText: 'Save Roles'
    },
    smart_suggestions: [
      'Create and customize roles to match how your practice operates—each role can have its own set of permissions.',
      'You can add as many roles as needed, from clinical to administrative, and adjust access levels anytime.',
      'Once your team is added, assign users to these roles to control what each person can view or manage.'
    ]
  },
  onboarding__staff_and_permissions__staff_invitation: {
    component: withWizardStep(WizardStaff),
    headerOptions: {
      stepTitle: 'Staff invitation',
      stepSubtitle:
        'Set up your practice team by adding members and defining their roles within Myriad Health.',
      buttonText: 'Save Staff'
    },
    smart_suggestions: [
      'Add your key team members first—each will receive an email invite to join with their assigned role.',
      'Prioritize staff who will help complete onboarding tasks, such as administrators, billers, or lead practitioners.',
      'Once added, your team can log in immediately and continue setup steps assigned to their roles.'
    ]
  },
  onboarding__delegation__delegation: {
    component: withWizardStep(DelegationTasks),
    headerOptions: {
      stepTitle: 'Delegation',
      stepSubtitle:
        'Assign onboarding tasks to your team members and set due dates to keep setup on track and ensure a smooth go-live experience.',
      buttonText: 'Save Delegation'
    },
    smart_suggestions: [
      'Select any step or substep and assign it to a team member who will handle that part of the setup.',
      'You can also add a note or set a due date to give clear instructions and keep tasks on schedule.',
      'Delegate multiple steps or substeps to different users to share the workload and ensure everything is completed on time.'
    ]
  },
  onboarding__data_export__data_export: {
    component: withWizardStep(DataExport),
    headerOptions: {
      stepTitle: 'Data export',
      stepSubtitle:
        'Select your preferred data migration option. You can request a full export from your previous system, confirm that you have already begun the extraction process, or skip data import entirely.',
      buttonText: null
    },
    smart_suggestions: [
      'Request the most comprehensive data export available from your previous system to ensure all critical patient information transfers smoothly.',
      'If you need help with data conversion or mapping, our team offers professional data migration services to handle complex transitions.',
      'Make sure your exported data is as fresh and complete as possible before uploading — this ensures accurate patient records from day one.'
    ]
  },
  onboarding__data_import__organization: {
    component: withWizardStep(ImportFromOrganization),
    headerOptions: {
      stepTitle: 'Organization',
      stepSubtitle:
        'Import existing data from your organization to streamline setup and reduce manual data entry.',
      buttonText: 'Continue'
    },
    smart_suggestions: [
      'Select a location from your organization to import existing data like patients, services, macros, packages, and products.',
      'Choose "All data" to import everything at once, or select "Specific data" to pick exactly what you need.',
      'You can skip importing if you prefer to start fresh with your new practice setup.'
    ]
  },
  onboarding__data_import__external: {
    component: withWizardStep(OnboardingImports),
    headerOptions: {
      stepTitle: 'External',
      stepSubtitle:
        'Accurate data entry is critical to a successful go-live. Please review the following options carefully before proceeding',
      buttonText: 'Start Import'
    },
    smart_suggestions: [
      "Select how you'd like to import your existing patient data — either upload a CSV file or use a software export from your previous system.",
      'Enter the estimated number of patients and double-check your file for accuracy before uploading to ensure smooth onboarding and correct billing data.',
      'You can review past imports or check supported EHR systems anytime to confirm compatibility and track which records were successfully added.'
    ]
  },
  onboarding__e_prescribe_setup__e_prescribe_setup: {
    component: withWizardStep(WizardEprescribe),
    headerOptions: {
      stepTitle: 'e-Prescribe setup',
      stepSubtitle:
        'Set up electronic prescribing for your practice using Veradigm Office, or opt out if not needed.',
      buttonText: 'Save E-Prescribe Settings'
    },
    smart_suggestions: [
      'Choose whether your practice will use E-Prescribe through sVeradigm to manage prescriptions electronically.',
      "If you select 'Create Veradigm Office,' follow the steps to enroll your practice and then add each licensed provider for setup.",
      "If your practice doesn't need e-prescribing, simply select 'I don't need E-Prescribe'; you can always enable it later from the Practice Hub."
    ]
  },
  onboarding__clearinghouse__billing_providers: {
    component: withWizardStep(BillingTable),
    headerOptions: {
      stepTitle: 'Billing providers',
      stepSubtitle: 'Add your billing provider details',
      buttonText: 'Save Billing Provider'
    },
    smart_suggestions: [
      'Add a billing provider profile for every Tax ID and NPI combination you use for claims, whether group, facility or individual. Make sure the name, Tax ID, NPI, taxonomy and address exactly match your payer enrollment forms.',
      'Each Billing Provider profile populates the Billing Provider section of your CMS-1500 claim and is used by payers to validate your submissions.',
      'If you are unsure which Tax ID and NPI combination to use for a Billing Provider profile, check your current CMS-1500 claims or payer enrollment letters before continuing.'
    ]
  },
  onboarding__clearinghouse__facilities: {
    component: withWizardStep(OnboardingFacilityTable),
    headerOptions: {
      stepTitle: 'Facilities',
      stepSubtitle: 'Add your practice facilities',
      buttonText: 'Save Facilities'
    },
    smart_suggestions: [
      'Add every location where your practice delivers care, including satellite offices, surgery centers and off-site locations that appear on claims. Use the exact facility name, NPI (if assigned) and address that payers have on file so your CMS-1500 Service Facility Location (Box 32) matches your enrollment records.',
      'If your practice operates in multiple locations, add every location where care is delivered. This ensures billing, reporting and clearinghouse routing remain accurate from day one.'
    ]
  },
  onboarding__clearinghouse__payers: {
    component: withWizardStep(OnboardingPayers),
    headerOptions: {
      stepTitle: 'Payers',
      stepSubtitle: 'Add your practice payers',
      buttonText: 'Save Payers'
    },
    smart_suggestions: [
      'Use the Add Payer… search bar to add all payers your practice bills today, including commercial plans, Medicare, Medicaid and secondary payers. These selections control where your claims, eligibility checks and ERAs are routed through the clearinghouse.',
      'If a payer is not listed, submit a request for a new payer ID through Myriad Support. Once approved, it will appear in your search results and can be added to your list. ',
      'Before moving on to EDI Enrollment, confirm that every active payer your practice bills is included here. This keeps claim submission and payment processing smooth. '
    ]
  },
  onboarding__clearinghouse__edi_enrollment: {
    component: withWizardStep(StepPlaceholder),
    headerOptions: {
      stepTitle: 'EDI enrollment',
      stepSubtitle: 'Enroll in Electronic Data Interchange (EDI) for claims processing',
      buttonText: 'Start EDI Enrollment'
    },
    smart_suggestions: [
      'EDI enrollment is crucial for going fully live on Myriad Health. Submit enrollments for every payer your practice bills and for each billing provider that sends claims.',
      'Until EDI is approved, payers may still send ERAs to your previous system. ',
      'Once enrollment is processed, ERAs will usually post only to Myriad Health Billing.',
      'EDI enrollment approval process can take a few days up to three weeks, depending on the payer.',
      'During this window, our expert billing support team is available to help you track enrollment status, confirm where ERAs are posting and know exactly when each payer is live. If you have any questions or concerns, reach out. You are very close to using Myriad Health as your full daily system. '
    ]
  },
  onboarding__schedule_of_fees__schedule_of_fees: {
    component: withWizardStep(ScheduleOfFees),
    headerOptions: {
      stepTitle: 'Schedule of fees',
      stepSubtitle:
        'Set accurate fees from the start to support reliable estimates. Myriad will analyze your ERAs and suggest updates to keep your schedule aligned with real reimbursements.',
      buttonText: 'Save Fee Schedule'
    },
    smart_suggestions: [
      'Start by selecting your practice specialty — this determines which services will appear in your Schedule of Fees.',
      'Choose the services you want to include, then review or edit their pricing before adding them to your practice.',
      'You can also apply a Fees Multiplier to adjust all service prices at once, helping you align your rates with your billing goals.'
    ]
  },
  onboarding__custom_forms__clinical_forms: {
    component: withWizardStep(OnboardingClinicalForms),
    headerOptions: {
      stepTitle: 'Clinical forms',
      stepSubtitle: 'Create and manage clinical forms',
      buttonText: 'Save Clinical Forms'
    },
    smart_suggestions: [
      "Create and customize clinical forms to fit your practice's workflow — start by naming your form and defining its purpose.",
      'You can organize your form with categories and sections to collect the right information efficiently during patient encounters.',
      'Forms can also be grouped and used together for a smoother experience when managing related clinical or intake tasks.'
    ]
  },
  onboarding__custom_forms__form_packets: {
    component: withWizardStep(OnboardingPackets),
    headerOptions: {
      stepTitle: 'Form packets',
      stepSubtitle: 'Create and manage packets for your practice',
      buttonText: 'Save Packets'
    }
  },

  onboarding__kiosk_setup__kiosk_setup: {
    component: withWizardStep(WizardKiosks),
    headerOptions: {
      stepTitle: 'Kiosk setup',
      stepSubtitle: `Configure and activate kiosks to streamline patient intake. Once added, you'll be able to manage kiosk devices, monitor their status, and keep your front-desk workflow running smoothly.`,
      buttonText: 'Add kiosk'
    },
    smart_suggestions: [
      'Register and manage all kiosks used in your practice to streamline patient check-ins and other front-desk activities.',
      'You can add a new kiosk by entering its verification code, naming it, and assigning staff members who will manage its requests.',
      'Each kiosk can be edited, activated, or disabled at any time — keeping full control over which terminals are paired and in use.'
    ]
  },

  // Live Training Steps
  onboarding__kickoff_live_training__kickoff_live_training: {
    component: withWizardStep(LiveTrainingStep),
    headerOptions: {
      stepTitle: 'Kickoff live training',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'Start Kickoff Training'
    },
    smart_suggestions: [
      'Confirm your setup is complete before the kickoff call to maximize training time.',
      'Create a test patient that all staff members can use during training sessions.',
      'Ensure staff permissions and assignments are set up correctly before the call.'
    ]
  },
  onboarding__front_office_live_training__front_office_live_training: {
    component: withWizardStep(LiveTrainingStep),
    headerOptions: {
      stepTitle: 'Front office live training',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'Start Front Office Training'
    },
    smart_suggestions: [
      'Complete healthcare program setup including GFE and Pre-authorized healthcare before training.',
      'Test your card reader and virtual terminal to ensure smooth payment processing.',
      'Upload your schedule of fees and set up self-scheduling if applicable.'
    ]
  },
  onboarding__clinical_live_training__clinical_live_training: {
    component: withWizardStep(LiveTrainingStep),
    headerOptions: {
      stepTitle: 'Clinical live training',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'Start Clinical Training'
    },
    smart_suggestions: [
      'Ensure your clinical forms and templates are built and verified by the principal user.',
      'Add codes and products if 3D charting is enabled to enable the checkout flow.',
      'Watch the product tour video before the training session.'
    ]
  },
  onboarding__back_office_live_training__back_office_live_training: {
    component: withWizardStep(LiveTrainingStep),
    headerOptions: {
      stepTitle: 'Back office live training',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'Start Back Office Training'
    },
    smart_suggestions: [
      'Submit EDI enrollments and verify your schedule of fees before the billing training.',
      'Ensure the full data import is verified by the principal user.',
      'Practice filing a test claim and running a patient payment before the session.'
    ]
  },
  onboarding__hard_launch_live_training__hard_launch_live_training: {
    component: withWizardStep(LiveTrainingStep),
    headerOptions: {
      stepTitle: 'Hard launch live training',
      stepSubtitle:
        'Review the requirements and set due dates based on your confirmed training schedule. To request a change to a confirmed training date, contact your dedicated onboarding team.',
      buttonText: 'Start Hard Launch Training'
    },
    smart_suggestions: [
      'Ensure 100% of onboarding and training is complete before hard launch.',
      'Your practice should be ready for live charting and patient payments.',
      'This is the final training before going fully live with Myriad Health.'
    ]
  },

  onboarding__review_review: {
    component: withWizardStep(StepperReview),
    headerOptions: {
      stepTitle: 'Review',
      stepSubtitle: 'Final review of all onboarding steps before going live.',
      buttonText: 'Complete Onboarding'
    }
  }
};
